import React, { useState, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Typography, Button, Stack } from "@altimateai/lego";
import { getLangfuseQueries } from "../services/langfuseQueries";
import { Loader } from "../components/Loader";
import { LangfuseDateFilters } from "./components/LangfuseDateFilters";
import { SortHeader, SortColumn, SortDirection } from "./components/SortHeader";
import { FilterableHeader } from "./components/FilterableHeader";
import { QueryRow } from "./components/QueryRow";
import { STATUS_OPTIONS, getDefaultDates, PAGE_SIZE, getDisplayStatus } from "./utils/queryUtils";

export const Langfuse: React.FC = () => {
  const [expandedQueryId, setExpandedQueryId] = useState<string | null>(null);
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [sortColumn, setSortColumn] = useState<SortColumn>("executed_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState<string>(defaultDates.start);
  const [endDate, setEndDate] = useState<string>(defaultDates.end);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["langfuseQueries", startDate, endDate],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      getLangfuseQueries({
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page: pageParam,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: lastPage => (lastPage.has_more ? lastPage.current_page + 1 : undefined),
  });

  const allQueries = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page.queries);
  }, [data]);

  const langfuseProjectId = data?.pages[0]?.langfuse_project_id;

  const tenantsInData = useMemo(() => {
    const tenants = new Set<string>();
    allQueries.forEach(q => tenants.add(q.tenant));
    return Array.from(tenants).sort();
  }, [allQueries]);

  const uniqueTables = useMemo(() => {
    const tables = new Set<string>();
    allQueries.forEach(q => q.tables.forEach(t => tables.add(t)));
    return Array.from(tables).sort();
  }, [allQueries]);

  const usersInData = useMemo(() => {
    const users = new Set<string>();
    allQueries.forEach(q => {
      if (q.user_id) users.add(q.user_id);
    });
    return Array.from(users).sort();
  }, [allQueries]);

  const filteredQueries = useMemo(() => {
    const filtered = allQueries.filter(q => {
      if (selectedTenants.length > 0 && !selectedTenants.includes(q.tenant)) return false;
      if (selectedTables.length > 0 && !q.tables.some(t => selectedTables.includes(t)))
        return false;
      if (selectedUsers.length > 0 && (!q.user_id || !selectedUsers.includes(q.user_id)))
        return false;
      if (selectedStatuses.length > 0) {
        const queryStatus = getDisplayStatus(q);
        if (!selectedStatuses.includes(queryStatus)) return false;
      }
      return true;
    });

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortColumn) {
        case "executed_at":
          comparison = new Date(a.executed_at).getTime() - new Date(b.executed_at).getTime();
          break;
        case "duration_ms":
          comparison = (a.duration_ms || 0) - (b.duration_ms || 0);
          break;
        case "status":
          comparison = a.status.localeCompare(b.status);
          break;
        case "tenant":
          comparison = a.tenant.localeCompare(b.tenant);
          break;
        case "tables":
          comparison = a.tables.join(",").localeCompare(b.tables.join(","));
          break;
        case "user_id":
          comparison = (a.user_id || "").localeCompare(b.user_id || "");
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return filtered;
  }, [
    allQueries,
    selectedTenants,
    selectedTables,
    selectedUsers,
    selectedStatuses,
    sortColumn,
    sortDirection,
  ]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const setPresetDateRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().split("T")[0]);
    setEndDate(end.toISOString().split("T")[0]);
  };

  const handleQueryClick = (queryId: string) => {
    setExpandedQueryId(expandedQueryId === queryId ? null : queryId);
  };

  return (
    <div className="al-p-6 al-max-w-full al-mx-auto">
      <Typography variant="h3" className="al-mb-4">
        Langfuse ClickHouse Queries
      </Typography>

      <Stack direction="row" align="center" justify="end" gap="md" className="al-mb-6 al-flex-wrap">
        <LangfuseDateFilters
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onPresetClick={setPresetDateRange}
          onClearClick={() => setPresetDateRange(7)}
        />
      </Stack>

      {isLoading ? (
        <Loader label="Loading queries..." />
      ) : (
        <div className="al-space-y-4">
          <div className="al-bg-white al-rounded-lg al-border al-overflow-hidden">
            <table className="al-w-full">
              <thead className="al-bg-gray-100">
                <tr>
                  <th className="al-p-3 al-text-left al-text-sm al-font-medium">Query</th>
                  <FilterableHeader
                    options={uniqueTables.map(t => ({ value: t, label: t }))}
                    selectedValues={selectedTables}
                    onSelectionChange={setSelectedTables}
                  >
                    Tables
                  </FilterableHeader>
                  <FilterableHeader
                    options={tenantsInData.map(t => ({ value: t, label: t }))}
                    selectedValues={selectedTenants}
                    onSelectionChange={setSelectedTenants}
                  >
                    Tenant
                  </FilterableHeader>
                  <FilterableHeader
                    options={usersInData.map(u => ({ value: u, label: u }))}
                    selectedValues={selectedUsers}
                    onSelectionChange={setSelectedUsers}
                  >
                    User
                  </FilterableHeader>
                  <FilterableHeader
                    options={STATUS_OPTIONS}
                    selectedValues={selectedStatuses}
                    onSelectionChange={setSelectedStatuses}
                  >
                    Status
                  </FilterableHeader>
                  <SortHeader
                    column="duration_ms"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Duration
                  </SortHeader>
                  <SortHeader
                    column="executed_at"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Time
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {filteredQueries.map(query => (
                  <QueryRow
                    key={query.id}
                    query={query}
                    isExpanded={expandedQueryId === query.id}
                    langfuseProjectId={langfuseProjectId}
                    onQueryClick={handleQueryClick}
                  />
                ))}
              </tbody>
            </table>

            {filteredQueries.length === 0 && (
              <div className="al-p-8 al-text-center al-text-gray-500">No queries found</div>
            )}
          </div>

          {hasNextPage && (
            <Stack direction="row" justify="center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? "Loading..." : "Load More"}
              </Button>
            </Stack>
          )}

          <div className="al-text-center al-text-sm al-text-gray-500">
            Showing {filteredQueries.length} queries
            {data?.pages[0]?.total_pages && data.pages[0].total_pages > 1 && (
              <span>
                {" "}
                (Page {data.pages.length} of {data.pages[0].total_pages})
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
